import { warmDialog } from "../compat/jquery-ui-dialog";
import { isConstrainedNetwork } from "../compat/constrained-network";
/**
 * Product-page processing + render pipeline — verbatim port of v1
 * process_product_page.js (research/02; REQ-BOOT-9/10/11). The ATC pieces
 * (HandleAddToCartEvent, HandleProductFormAndAddToCartButton, the stored
 * calculator-element reference) live in checkout/ per SPEC §2.1; render
 * placement hands the element over via setCurrentCalculatorElement.
 *
 * Module-eval side effects (REQ-BOOT-1c): jquery-ui dialog CSS + widget are
 * loaded here (registering .dialog() on the BUNDLED jQuery only — theme
 * theme jQuery never gets it — no longer relevant since DIV-004), and window.cpc.renderCalculator
 * is defined at parse time.
 */
import { appState } from "../state/app-state";
import { containerDirAttr } from "../i18n/translate";
import { buildCalculator } from "../elements/build-calculator";
import {
  HandleProductFormAndAddToCartButton,
  setCurrentCalculatorElement,
} from "../checkout";
import { PreventCheckoutOnEnter } from "../common/common-utils";
import { globalHelper_ReportError } from "../api/logs-error";
import { initializeFileUploadHandlers } from "../api/file-upload";
import { loadScripts } from "./load-style";
import { firstLoad } from "../pricing";
import { fetchCalculatorByProduct } from "../api/calculator-by-product";
import { LoadCalculator } from "../api/calculator-app";
import {
  completeClientPerformanceFailure,
  completeClientPerformanceNoCalculatorAssigned,
  completeClientPerformanceSuccess,
  markClientPerformance,
  markClientPerformanceVisible,
} from "../performance/client-performance-lifecycle";

import "jquery-ui/themes/base/dialog.css";
import $ from "jquery";
import { setTranslations } from "../i18n/translate";
import { applyStoreCss, requestStyles, stylesReady } from "./styles";
import { markCalculatorPage } from "./calculator-page-marker";
import { readOptionNames } from "../checkout/variant-resolution";

let currentCartElement: any = null;

export async function ProcessProductPage(): Promise<void> {
  // Telemetry: the loader boundary — the calculator lookup starts next.
  markClientPerformance("loader_started");
  try {
    var calculatorId;

    // this.productForm = productForm;
    // this.submitButton = submitButton;

    if (appState.cpc_productId > 0) {
      var calcProduct = await fetchCalculatorByProduct();
      if (
        calcProduct != null &&
        calcProduct.calcUniqueIden != null &&
        calcProduct.calcUniqueIden != undefined &&
        calcProduct.calcUniqueIden != ""
      ) {
        console.log("CalculatorId found from API");
        calculatorId = calcProduct.calcUniqueIden;
      }
    }
    //Fall back to old way if we cant find the calculatorId from the serer
    if (calculatorId == null || calculatorId == 0 || calculatorId == undefined) {
      var customCalculatorDiv = document.getElementById("custom-calculator");
      // $(null) is a valid empty set at runtime (v1 semantics); typings say no
      calculatorId = $(customCalculatorDiv as any).attr("data-calculatorid");

      if (calculatorId != null && calculatorId != undefined)
        console.log("CalculatorId found from Fallback Method", calculatorId);
      else {
        // The lookup answered and there is no calculator for this page: the
        // NOT-APPLICABLE terminal. Reported (never silent) so a store whose
        // calculators are assigned to no products is visible; the API keeps it
        // out of every rate. Telemetry-only — the log, the return and the
        // control flow below are unchanged.
        console.log("No calculator found");
        completeClientPerformanceNoCalculatorAssigned();
        return;
      }
    }

    if (calculatorId != null && calculatorId != undefined) {
      appState.cpc_calculatorId = calculatorId;
      console.log("calculator found", calculatorId);
      console.log("product Id", appState.cpc_productId);

      // DIV-024 started the stylesheets here, the moment a calculator was known to
      // exist; DIV-034 starts plugins.css at boot (DetectPage), and DIV-035 needs
      // the payload for the store's own CSS — so nothing is left to start here.
      // (`requestStyles()` is still safe to call from anywhere: it dedupes.)
      requestStyles();
      // DIV-039: from here on the theme-targeting rules of the base stylesheet apply.
      markCalculatorPage();

      // Fetch calculator data
      let json: any;
      try {
        const response = await LoadCalculator(calculatorId, appState.cpc_isPreview);
        if (response.status === 204) {
          // No content, handle accordingly. The request SUCCEEDED and returned
          // no calculator, so this is the same NOT-APPLICABLE terminal as the
          // fallback path above — reported, with the id we did resolve, and
          // never counted as a failure.
          console.log("No content returned for the calculator from API.");
          completeClientPerformanceNoCalculatorAssigned({ calculatorId });
          return; // or some default value
        }
        json = await response.json();
      } catch (error) {
        // Report, then rethrow the SAME error — the uncaught rejection on this
        // path is legacy behavior (BUG-010) and must stay byte-identical.
        completeClientPerformanceFailure("configuration_request_failed", error, undefined, "calculator_app");
        throw error;
      }
      markClientPerformance("bundle_ready");
      appState.cpc_data = json.calculatorData;
      // Mutate the existing array instead of reassigning — other references
      // (e.g. pricing/) hold a reference to the original
      // array; reassignment would break those stale bindings. (REQ-STATE-1)
      appState.cpc_metaFields.length = 0;
      if (json.metaFields) {
        json.metaFields.forEach((item: any) => appState.cpc_metaFields.push(item));
      }
      appState.cpc_waitforcartconfirmation = json.waitForCartConfirmation;
      appState.cpc_cartDrawerEnabled = json.configuration?.enableCartDrawerIntegration === true;
      appState.cpc_configData = json.configuration;
      appState.calculatorStyling = json.styling;
      appState.addToCartSelector = json.addToCartSelector;
      // #chunk3: merchant opt-in for client-side Shopify.currency conversion (currency v1).
      appState.nativeCurrencyEnabled = json.nativeCurrencyEnabled === true;
      // Translations v1: absent from the payload unless the store is entitled, switched on, and
      // has something stored for this shopper's locale — so this is null for every store today.
      setTranslations(json.translations?.map);
      // DIV-035: the store's own CSS came with the payload (undefined = older API → request it).
      applyStoreCss(json.customCss);
      markClientPerformance("configuration_ready");

      console.log("addToCartSelector", appState.addToCartSelector);

      console.log("meta-fields", appState.cpc_metaFields);

      // Initial render
      await internalRenderCalculator(calculatorId, appState.cpc_configData, appState.calculatorStyling, appState.addToCartSelector, appState.cpc_data);
      // Mirrors internalRenderCalculator's own bail-out guard. Reported HERE and
      // not inside that shared function, because window.cpc.renderCalculator
      // (preview) also calls it and must never consume this page's one report.
      if (!appState.cpc_data || !calculatorId) completeClientPerformanceFailure("invalid_configuration");
      else completeClientPerformanceSuccess({ calculatorId });
    }
  } catch (error) {
    // Any unexpected bootstrap exception: report a SAFE code (never the
    // message or stack) and rethrow so legacy error behavior is unchanged.
    completeClientPerformanceFailure("runtime_error", error);
    throw error;
  }
}

async function internalRenderCalculator(calculatorId: any, configData: any, styling: any, addToCartSelectorElement: any, calculatorData: any): Promise<void> {
  if (!calculatorData || !calculatorId) return;
  markCalculatorPage(); // DIV-039 — also covers the preview entry (window.cpc.renderCalculator)

  if (!configData) {
    configData = {
      smartVariantAutoDeletion: false,
      alertMessageNumberInput:
        "Please enter a value between range {input_range_min} and {input_range_max}",
      thousandSeperator: 0,
      decimalSeperator: 0,
    };
  }

  appState.cpc_data = calculatorData;
  appState.cpc_configData = configData;
  appState.calculatorStyling = styling;
  appState.addToCartSelector = addToCartSelectorElement;

  localStorage.setItem("cpc_configData", JSON.stringify(configData));

  loadScripts();
  await fetchProductMetaData();

  var priceElements = [
    "product-price",
    "product-single__price",
    "selector-wrapper",
    "current_price",
    "product__price",
    "shopify-payment-button",
    "single-option-selector",
  ];
  priceElements.forEach((elem) => {
    var elementToHide = $("." + elem);
    elementToHide.addClass("hide-element");
  });

  //Load css
  //Change the old element so that you can create new element with same id
  var variantSelector = document.querySelector('select[name="id"]');
  if (variantSelector !== null) {
    variantSelector.setAttribute("name", "oldId");
  }

  //Keep replacing all name[id] to oldId to prevent conflicts
  while (true) {
    var variantSelector = document.querySelector('input[name="id"]');
    if (variantSelector !== null) {
      variantSelector.setAttribute("name", "oldId");
    }
    else {
      break;
    }
  }

  // Load base calculator CSS always — plugins.css in flight since boot (DIV-034),
  // the store's CSS since the payload arrived (DIV-035); this is the same wait
  // point as before. The preview entry (window.cpc.renderCalculator) has no
  // payload, so for it this still requests store/style/app.
  await stylesReady();

  //To hide the loader once page has loaded
  var customCalcBlock = $("#custom-calculator");
  customCalcBlock.hide();

  console.log("Rendering calculator...");

  var builtData = buildCalculator(calculatorData);

  currentCartElement = await HandleProductFormAndAddToCartButton(addToCartSelectorElement);

  var borderClass =
    styling?.border === "rounded" ? "class='rounded-edges'" : "";

  var appBlockContainer = document.querySelector(".cpc-calculator-target");
  var calculatorBlockContainer = document.getElementById("calculator-block");
  markClientPerformance("mount_target_ready");

  var calculatorHtml = "<div id='calculator' data-calcid='" +
          calculatorId +
          "' " +
          borderClass +
          containerDirAttr() +
          " style='background-color:" +
          styling.upperBackground +
          "'>" +
          builtData +
          "</div>";

  // Remove any existing calculator before rendering a new one
  var existingCalculator = document.getElementById("calculator");
  if (existingCalculator) {
    console.log("Existing calculator found — removing old calculator before rendering new one.");
    existingCalculator.remove();
  }

  if (appBlockContainer) {
    $(appBlockContainer).html(calculatorHtml);
    setCurrentCalculatorElement(appBlockContainer);
  } else if (calculatorBlockContainer) {
    $(calculatorBlockContainer).html(calculatorHtml);
    setCurrentCalculatorElement(calculatorBlockContainer);
  } else if (currentCartElement) {
    const calculatorContainer = $(
      calculatorHtml)
      .insertBefore($(currentCartElement).parent());
    setCurrentCalculatorElement(calculatorContainer[0]);
  }

  // DIV-033: mirror the boot-time variant onto #variantIdCustom's VALUE the
  // moment it exists in the DOM, instead of leaving the placeholder "0"
  // until an actual Add to Cart click (see buildVariantElement's comment).
  // find-product-form.ts's variant:changed/shopify:product:select listeners
  // keep this current after a later switch; this is the boot-time half.
  var variantIdCustomEl = document.getElementById("variantIdCustom") as HTMLInputElement | null;
  if (variantIdCustomEl) variantIdCustomEl.value = String(appState.baseVariantId);

  markClientPerformanceVisible();

  //Add loading animation to the root(body)
    if (appState.calculatorStyling.loaderText === null) appState.calculatorStyling.loaderText = "";
  // DIV-038: loading="lazy" — the overlay is display:none until an Add to Cart,
  // so the spinner is fetched when it is first shown, not on every render.
  var loadingElem =
    "<div class='loading-anim-cpc' style='display:none; text-align:center; width:100%;'><img src='https://app.custompricecalculator.com/loading-spinner.gif' loading='lazy' style='width:60px; height:60px; max-width:60px; display:block; margin:0 auto;'><p id='cpc_loader_text'>" +
    appState.calculatorStyling.loaderText +
    "</p></div>";
  $("body").append(loadingElem);

  //Custom Store JS
  var shopCustomJsPath =
    appState.baseCalculatorNewApiUrl + "store/script/app?shop=" + appState.cpc_shopUrl;
  if (shopCustomJsPath != null && shopCustomJsPath.length > 0) {
    var customStoreJSRef = document.createElement("script");
    customStoreJSRef.setAttribute("type", "text/javascript");
    customStoreJSRef.setAttribute("src", shopCustomJsPath);
    customStoreJSRef.defer = true;
    customStoreJSRef.setAttribute("crossorigin", "anonymous");
    document
      .getElementsByTagName("head")[0]!
      .appendChild(customStoreJSRef);
  }

  firstLoad(addToCartSelectorElement);
  PreventCheckoutOnEnter();
  initializeFileUploadHandlers();
  markClientPerformance("calculator_interactive");
  // DIV-031: have jQuery UI's dialog in place before a shopper can reach an error path —
  // except on a constrained connection (DIV-038), where it loads on demand instead.
  if (!isConstrainedNetwork()) warmDialog();

  console.log("Calculator render complete.");
}

// Boot runs before the theme's own name="id" element is renamed to "oldId"
// (see the rename loop in ProcessProductPage below), so the theme's original
// variant-picker markup is still on the page here and reflects whichever
// variant the theme has pre-selected — including a non-first one (from a
// URL param, or the theme's own default). Reads the three shapes themes use
// for the Shopify variant id: a checked radio (Horizon-style
// <variant-radios>), a <select>, or a plain/hidden input.
function getSelectedVariantIdFromDom(): string | null {
  var checkedInput = document.querySelector('input[name="id"]:checked') as HTMLInputElement | null;
  if (checkedInput && checkedInput.value) return checkedInput.value;

  var select = document.querySelector('select[name="id"]') as HTMLSelectElement | null;
  if (select && select.value) return select.value;

  var input = document.querySelector('input[name="id"]') as HTMLInputElement | null;
  if (input && input.value) return input.value;

  return null;
}

/**
 * DIV-030: the product's `.js` (Shopify's own endpoint, variants and option names)
 * depends only on the page URL, yet was requested only after the calculator config
 * had arrived — one serial round-trip to the store on every calculator page, most
 * of the ~340 ms between config and mount. DetectPage now starts it on product
 * pages the moment it starts the calculator resolve, and fetchProductMetaData
 * consumes the in-flight promise. The URL, its cache-buster and the preview-key
 * handling are unchanged; only WHEN the request goes out moves.
 */
let productMetaDataInFlight: Promise<Response> | undefined;

function productMetaDataUrl(): string {
  let cpc_product_page_url = window.location.origin + window.location.pathname;
  let previewKey: any = new URLSearchParams(window.location.search).toString();
  if (previewKey === undefined) previewKey = "";
  let hasPreviewKey = previewKey.startsWith("preview_key");
  var productFetchUrl = cpc_product_page_url + ".js?d=" + Math.random();
  if (hasPreviewKey === true)
    productFetchUrl = cpc_product_page_url + ".js?" + previewKey + "&d=" + Math.random();
  return productFetchUrl;
}

export function prefetchProductMetaData(): void {
  if (productMetaDataInFlight === undefined && appState.cpc_isPreview === false && appState.cpc_productId > 0) {
    productMetaDataInFlight = fetch(productMetaDataUrl());
    // A rejection is handled where the response is consumed (below), exactly as
    // before; this only stops an unconsumed prefetch from surfacing as unhandled.
    productMetaDataInFlight.catch(() => undefined);
  }
}

export async function fetchProductMetaData(): Promise<void> {
  try {
    if (appState.cpc_isPreview === false) {
      const inflight = productMetaDataInFlight;
      productMetaDataInFlight = undefined;

      await (inflight ?? fetch(productMetaDataUrl()))
        .then((response) => response.json())
        .then(async function (cpc_product_meta_data) {
          if (cpc_product_meta_data !== undefined) {
            var default_variant = cpc_product_meta_data.variants[0];
            // DIV-029: prefer the variant the theme has ALREADY pre-selected
            // over always variants[0]. Themes on Horizon 4.x (and others) can
            // render with a non-first variant pre-selected on first paint —
            // reading variants[0] unconditionally then boots CPC on a
            // different variant's price than the one the shopper actually
            // sees, and that wrong price feeds both the on-page ticker and
            // (if the shopper never touches the variant picker before Add to
            // Cart) the checkout line-item price itself. Fixes the boot-time
            // half of issue #164 (ScatterMats — 80cm pre-selected, but the
            // 67cm variant's $39/m price was used for a 2m order, pricing it
            // at $78 instead of $96).
            if (Array.isArray(cpc_product_meta_data.variants)) {
              var selectedVariantId = getSelectedVariantIdFromDom();
              if (selectedVariantId !== null) {
                var matched = cpc_product_meta_data.variants.find(function (v: any) {
                  return v && String(v.id) === selectedVariantId;
                });
                if (matched !== undefined) default_variant = matched;
              }
            }
            if (default_variant !== undefined) {
              appState.cpc_product_default_price = default_variant.price / 100;
              appState.baseVariantId = default_variant.id;
              // DIV-032: resolve the boot-time variant's true availability
              // here too, alongside its price — see find-product-form.ts's
              // use of this to correct a submitButton clone born disabled by
              // an unrelated theme-side race (issue #164 follow-up; single-
              // variant "by the meter" products at ScatterMats never fire a
              // variant switch event, so DIV-015's reactive sync never runs).
              appState.cpc_product_default_available =
                typeof default_variant.available === "boolean" ? default_variant.available : null;
            }

            // DIV-026: keep the full variant list (id + option1/2/3) and the
            // product's option names around, not just variants[0] — the CT
            // Non-Variant ATC path needs them to resolve a size-matched
            // variant instead of always submitting the default one.
            // DIV-042: Shopify serves `options` as { name, position, values }
            // objects; readOptionNames takes a plain string first, then the
            // object's name (only strings were kept before, so this was
            // always empty in production).
            appState.cpc_productOptionNames.length = 0;
            readOptionNames(cpc_product_meta_data.options).forEach((name) => {
              appState.cpc_productOptionNames.push(name);
            });
            appState.cpc_productVariants.length = 0;
            if (Array.isArray(cpc_product_meta_data.variants)) {
              cpc_product_meta_data.variants.forEach((v: any) => {
                if (v && typeof v.id !== "undefined") {
                  appState.cpc_productVariants.push({
                    id: v.id,
                    option1: v.option1 ?? null,
                    option2: v.option2 ?? null,
                    option3: v.option3 ?? null,
                    available: typeof v.available === "boolean" ? v.available : null,
                  });
                }
              });
            }
          }
        });
      console.log("shopify product price", appState.cpc_product_default_price);
    } else {
      console.log("Calculator in preview mode");
      appState.cpc_product_default_price = 10;
    }
  } catch (e) {
    var errMsg = "Shopify Price Error:. ProductId =" + appState.cpc_productId;
    globalHelper_ReportError(e, errMsg);
    appState.cpc_product_default_price = 0;

    setTimeout(function () {
      window.location.reload();
    }, 3000);
  }
}

window.cpc.renderCalculator = async function (calculatorId: any, configData: any, styling: any, addToCartSelectorElement: any, calculatorData: any) {
  if (!appState.cpc_isPreview) {
    console.warn("Calculator cannot be rendered directly outside preview mode.");
    return;
  }
  return internalRenderCalculator(calculatorId, configData, styling, addToCartSelectorElement, calculatorData);
};
